<template>
  <main
    class="page"
    role="main"
    aria-labelledby="page-title"
  >
    <header class="page-header">
      <h1
        id="page-title"
        class="h1"
      >
        Passwort zurücksetzen
      </h1>
    </header>

    <section class="card">
      <div
        v-if="!token && !loading && !success"
        class="notice notice-danger"
        role="alert"
      >
        <p>Kein Token gefunden. Bitte den Link aus der E-Mail verwenden.</p>
        <p class="mt-8">
          <router-link :to="{ name: 'ForgotPassword' }">
            Reset-Link erneut anfordern
          </router-link>
        </p>
      </div>

      <form
        v-else
        novalidate
        class="form"
        @submit.prevent="submit"
      >
        <div class="field">
          <label for="password">Neues Passwort</label>
          <input
            id="password"
            v-model="password"
            type="password"
            required
            autocomplete="new-password"
            :disabled="loading"
            placeholder="Mindestens 8 Zeichen"
          >
          <p
            v-if="validationError && validationError.type === 'length'"
            class="help danger"
            role="alert"
          >
            {{ validationError.message }}
          </p>
        </div>

        <div class="field">
          <label for="passwordConfirm">Passwort wiederholen</label>
          <input
            id="passwordConfirm"
            v-model="passwordConfirm"
            type="password"
            required
            autocomplete="new-password"
            :disabled="loading"
            placeholder="Passwort wiederholen"
          >
          <p
            v-if="validationError && validationError.type === 'match'"
            class="help danger"
            role="alert"
          >
            {{ validationError.message }}
          </p>
        </div>

        <button
          type="submit"
          class="btn btn-primary"
          :disabled="loading || !token || !!validationError"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          />
          {{ loading ? 'Setze...' : 'Passwort setzen' }}
        </button>

        <p
          v-if="success"
          class="notice notice-success mt-12"
          role="status"
        >
          Dein Passwort wurde erfolgreich geändert. Du wirst zum
          <router-link :to="{ name: 'Login' }">
            Login
          </router-link>
          weitergeleitet.
        </p>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
      </form>
    </section>
  </main>
</template>
<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { resetPassword } from '@/api/password';
import api from '@/services/api';

const router = useRouter();

const token = ref('');
const password = ref('');
const passwordConfirm = ref('');
const loading = ref(false);
const error = ref('');
const success = ref(false);
const validationError = ref(null);

/**
 * Extrahiere Token sicher aus URL: erst Query-Parameter, sonst Fragment (#token=...)
 * Entferne danach das Token aus der sichtbaren URL (history.replaceState)
 */
onMounted(async () => {
  try {
    const url = new URL(window.location.href);
    // Query param prüfen
    const qToken = url.searchParams.get('token');
    if (qToken) {
      token.value = qToken;

      // Versuchszähler/Status im Backend tracken
      await trackOpen(qToken);

      // Entferne token aus Query ohne Reload
      url.searchParams.delete('token');
      history.replaceState(null, '', url.toString());
      return;
    }

    // Fragment prüfen: #token=...
    if (window.location.hash) {
      const hash = window.location.hash.substring(1); // entferne '#'
      const params = new URLSearchParams(hash);
      const hToken = params.get('token');
      if (hToken) {
        token.value = hToken;

        // Versuchszähler/Status im Backend tracken
        await trackOpen(hToken);

        // Entferne Fragment
        const cleanedUrl = window.location.href.split('#')[0];
        history.replaceState(null, '', cleanedUrl);
        return;
      }
    }
  } catch (e) {
    // Bei Fehler Token leer lassen und UI zeigen
    console.warn('Token extraction failed', e);
  }
});

watch([password, passwordConfirm], () => {
  validationError.value = null;
  if (password.value && password.value.length < 8) {
    validationError.value = { type: 'length', message: 'Passwort mindestens 8 Zeichen.' };
  } else if (password.value && passwordConfirm.value && password.value !== passwordConfirm.value) {
    validationError.value = { type: 'match', message: 'Passwörter stimmen nicht überein.' };
  }
});

async function trackOpen(tok) {
  try {
    const { data } = await api.get('/password/reset/open', { params: { token: tok } });
    if (data && data.valid === false) {
      // Benutzerfreundliche Meldungen für ungültige Zustände
      if (data.code === 'expired') {
        error.value = 'Der Link ist abgelaufen. Bitte fordere einen neuen Reset-Link an.';
      } else if (data.code === 'consumed') {
        error.value = 'Dieser Link wurde bereits verwendet. Bitte fordere einen neuen Reset-Link an.';
      } else {
        error.value = 'Ungültiger Link. Bitte fordere einen neuen Reset-Link an.';
      }
      // Token leeren → Formular deaktivieren
      token.value = '';
    }
  } catch (e) {
    // Netzwerk-/Serverfehler hier nicht blockierend behandeln
    // optional: console.warn('Track open failed', e);
  }
}

async function submit() {
  error.value = '';
  success.value = false;

  // zusätzliche Prüfung vor dem Aufruf
  if (!token.value) {
    error.value = 'Kein Token vorhanden. Bitte benutze den Link aus der E-Mail.';
    return;
  }
  if (!password.value || !passwordConfirm.value) {
    validationError.value = { type: 'match', message: 'Bitte Passwort und Bestätigung ausfüllen.' };
    return;
  }
  if (password.value.length < 8) {
    validationError.value = { type: 'length', message: 'Passwort mindestens 8 Zeichen.' };
    return;
  }
  if (password.value !== passwordConfirm.value) {
    validationError.value = { type: 'match', message: 'Passwörter stimmen nicht überein.' };
    return;
  }

  loading.value = true;
  try {
    const res = await resetPassword(token.value, password.value, passwordConfirm.value);
    const data = res && (res.data ?? res) ? (res.data ?? res) : res;
    // API gibt oft { success: true } oder enthält message
    if (data && (data.success === false || data.error === true)) {
      error.value = data.message || 'Passwort konnte nicht zurückgesetzt werden.';
      return;
    }

    // Erfolgreich
    success.value = true;
    // Formularfelder löschen
    password.value = '';
    passwordConfirm.value = '';
    // Optional: Token nach Erfolg ungültig machen (lokal löschen)
    token.value = '';

    // Nach kurzer Zeit zum Login weiterleiten
    setTimeout(() => {
      router.push({ name: 'Login' }).catch(() => {});
    }, 1600);
  } catch (e) {
    const status = e?.response?.status;
    const respMsg = e?.response?.data?.message || e?.response?.data?.data?.message;
    if (status === 400 || status === 422) {
      error.value = respMsg || 'Ungültige oder abgelaufene Anforderung.';
    } else if (status === 429) {
      error.value = respMsg || 'Zu viele Anfragen. Bitte später erneut versuchen.';
    } else {
      error.value = e?.message || 'Es ist ein Fehler aufgetreten. Bitte später erneut versuchen.';
    }
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
/* V2 Tokens werden global als :root Variablen erwartet */
.page {
  padding: 16px;
  background: var(--bg, #F6F7F9);
  color: var(--text, #1F2937);
  min-height: calc(100vh - 0px);
}
.page-header {
  margin: 0 0 12px 0;
  border-bottom: 1px solid var(--border, #E0E3EA);
  padding-bottom: 8px;
}
.h1 {
  font-size: 20px;
  line-height: 24px;
  font-weight: 500;
  margin: 0;
}

.card {
  background: var(--surface, #FFFFFF);
  border: 1px solid var(--border, #E0E3EA);
  border-radius: 10px;
  box-shadow: 0 1px 0 rgba(0,0,0,.02);
  max-width: 480px;
  padding: 14px;
  margin: 16px auto 24px;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field label {
  display: block;
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 6px 0;
  color: var(--text, #1F2937);
}

.field input {
  width: 100%;
  height: 36px;
  padding: 8px;
  border: 1px solid var(--border, #E0E3EA);
  border-radius: 6px;
  background: var(--surface, #FFFFFF);
  color: var(--text, #1F2937);
  outline: none;
  transition: box-shadow .15s ease, border-color .15s ease;
}
.field input:focus {
  box-shadow: 0 0 0 2px var(--focus, rgba(79,70,229,.45));
  border-color: var(--primary, #4F46E5);
}

.help {
  font-size: 12px;
  line-height: 16px;
  margin: 6px 0 0 0;
  color: var(--muted, #6B7280);
}
.help.danger {
  color: var(--danger, #EF4444);
}

.btn {
  height: 40px;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid var(--primary, #4F46E5);
  background: transparent;
  color: var(--primary, #4F46E5);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease, border-color .15s ease;
}
.btn[disabled] {
  opacity: .6;
  cursor: default;
}
.btn-primary {
  background: var(--primary, #4F46E5);
  color: #fff;
  border-color: var(--primary, #4F46E5);
}
.btn-primary:hover {
  background: var(--primary-600, #4338CA);
  border-color: var(--primary-600, #4338CA);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: 2px solid rgba(255,255,255,.6);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
  vertical-align: -3px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.notice {
  border-radius: 6px;
  border: 1px solid var(--border, #E0E3EA);
  padding: 10px 12px;
  font-size: 14px;
  line-height: 20px;
}
.notice-success {
  border-color: rgba(16,185,129,.35);
  background: rgba(16,185,129,.08);
  color: var(--text, #1F2937);
}
.notice-danger {
  border-color: rgba(239,68,68,.35);
  background: rgba(239,68,68,.08);
  color: var(--text, #1F2937);
}

.mt-8 { margin-top: 8px; }
.mt-12 { margin-top: 12px; }
</style>